import type { AdminTicketDetailsDto, HomeDnaStatusDto } from "@/api/types";
import homeDnaPng from "@/assets/images/integrations/home-dna.png";
import { CheckIcon } from "@/components/Icons/CheckIcon";
import { BrandMark } from "@/design-system/BrandMark";
import { Button } from "@/design-system/Button";
import { Card, CardCenteredState } from "@/design-system/Card";
import { Inline } from "@/design-system/Inline";
import { Spinner } from "@/design-system/Spinner";
import { Stack } from "@/design-system/Stack";
import { Text } from "@/design-system/Text";
import { ticketMutations } from "@/queries/tickets/mutations";
import { useTranslation } from "@/translations";

export function TicketHomeDnaCard({
  ticket,
  homeDna,
}: {
  ticket: AdminTicketDetailsDto;
  homeDna: HomeDnaStatusDto;
}): React.ReactNode {
  const { t } = useTranslation();
  const uploadTicketMutation = ticketMutations.useSendTicketToHomeDna(ticket.id);
  const canSendHomeDnaTicket = ticket.address != null;

  return (
    <Card>
      <Inline justify="center">
        <BrandMark src={homeDnaPng} alt="Home DNA logo" proportion="panoramic" />
      </Inline>
      {uploadTicketMutation.isPending ? (
        <CardCenteredState>
          <Spinner size="sm" />
        </CardCenteredState>
      ) : (
        <Stack gap="md" align="center">
          {homeDna.state === "notStarted" && (
            <Text variant="body-spacious" tone="secondary" overflow="normal" data-testid="ticket-integration-status">
              {t("page.tickets.homedna.state")} {t("page.tickets.homedna.state.not-uploaded")}
            </Text>
          )}
          {homeDna.state === "failed" && (
            <Text variant="body-spacious" tone="secondary" overflow="normal" data-testid="ticket-integration-status">
              {t("page.tickets.homedna.state")}{" "}
              <Text variant="body-spacious" tone="danger-secondary" overflow="normal">
                {homeDna.errorDetails || t("page.tickets.homedna.state.error")}
              </Text>
            </Text>
          )}
          {homeDna.state === "sent" && (
            <Text
              as="div"
              variant="body-emphasis"
              tone="success"
              align="center"
              data-testid="ticket-integration-status"
            >
              <Inline gap="xs" align="center" wrap={false}>
                <CheckIcon size={16} />
                {t("page.tickets.homedna.state.success")}
              </Inline>
            </Text>
          )}

          {homeDna.state !== "sent" && (
            <Inline>
              <Button
                fullWidth
                onClick={() => uploadTicketMutation.mutate()}
                disabled={!canSendHomeDnaTicket}
                title={canSendHomeDnaTicket ? undefined : t("page.tickets.homedna.actions.send-ticket-disabled")}
              >
                {t("page.tickets.homedna.actions.send-ticket")}
              </Button>
            </Inline>
          )}
        </Stack>
      )}
    </Card>
  );
}
